import Icon from "@components/Icon";
import {DuplicateAppointment} from "@services/graphql/queries/appointment";
import moment from "moment-timezone";
import {useTranslation} from "@i18n/client";
import React from "react";

import {I18nNamespace} from "@i18n";

const AppointmentTile = ({
  thisAppt,
  otherAppt,
  isExisting,
  isSelected,
  onClick,
}: {
  thisAppt: Omit<DuplicateAppointment, "id">;
  otherAppt: Omit<DuplicateAppointment, "id">;
  isExisting: boolean;
  onClick: () => unknown;
  isSelected: boolean;
}) => {
  const thisApptDay = useDayString(
    thisAppt.slotTime,
    thisAppt.locationTimezone ?? moment.tz.guess(),
  );
  const otherApptDay = useDayString(
    otherAppt.slotTime,
    otherAppt.locationTimezone ?? moment.tz.guess(),
  );
  const apptDayStyles = thisApptDay === otherApptDay ? "" : "font-semibold";

  const thisApptTime = moment(thisAppt.slotTime)
    .tz(thisAppt.locationTimezone ?? moment.tz.guess())
    .format("h:mma z");
  const otherApptTime = moment(otherAppt.slotTime)
    .tz(thisAppt.locationTimezone ?? moment.tz.guess())
    .format("h:mma z");
  const apptTimeStyles = thisApptTime === otherApptTime ? "" : "font-semibold";

  const locationNameStyles =
    thisAppt.locationName === otherAppt.locationName ? "" : "font-semibold";

  const color = isSelected ? "bg-green-800 text-white" : "bg-gray-100";
  return (
    <button
      className={`flex flex-col p-4 gap-2.5 rounded-xl hover:cursor-pointer w-[167px] border-none ${color}`}
      onClick={onClick}
    >
      <div className="flex flex-row w-full justify-between">
        <AppointmentTileSelectionIndicator isSelected={isSelected} />
        {isExisting ? <ExistingTile isSelected={isSelected} /> : <NewTile />}
      </div>
      <div className="flex flex-col w-full gap-2 text-left">
        <div className="text-lg/[1.2]">
          <span className={apptDayStyles}>{thisApptDay},</span>
          <br />
          <span className={`whitespace-nowrap ${apptTimeStyles}`}>{thisApptTime}</span>
        </div>
        <div className={`line-clamp-2 ${locationNameStyles}`}>{thisAppt.locationName}</div>
      </div>
    </button>
  );
};
export default React.memo(AppointmentTile);

function useDayString(time: number, timezone: string): string {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const timeObj = moment(time).tz(timezone);
  const currentDay = moment(new Date());
  return timeObj.isSame(currentDay, "day")
    ? i18n.t("Today")
    : timeObj.isSame(currentDay.add(1, "day"), "day")
    ? i18n.t("Tomorrow")
    : timeObj.format("dddd");
}

const _AppointmentTileSelectionIndicator = ({isSelected}: {isSelected: boolean}) => {
  const styles = isSelected ? "bg-green-200" : "bg-white";
  return isSelected ? (
    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${styles}`}>
      <Icon
        icon="tick"
        className={`w-6 h-6 text-green-800 text-xs items-center justify-center inline-flex rounded-full ${styles}`}
      />
    </div>
  ) : (
    <div className="w-6 h-6 bg-white rounded-full" />
  );
};
const AppointmentTileSelectionIndicator = React.memo(_AppointmentTileSelectionIndicator);

const InternalExistingTile = ({isSelected}: {isSelected: boolean}) => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const color = isSelected ? "text-white" : "text-gray-800";
  return (
    <span className={`flex flex-col justify-center font-m text-[10px]/[12px] ${color}`}>
      {i18n.t("EXISTING", {ns: "web-scheduling"})}
    </span>
  );
};

const ExistingTile = React.memo(InternalExistingTile);

const InternalNewTile = () => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  return (
    <div className="flex flex-col justify-center font-m text-[10px]/[12px] bg-green-200 border border-green-300 text-green-800 rounded-full py-1 px-2">
      {i18n.t("NEW", {ns: "web-scheduling"})}
    </div>
  );
};
const NewTile = React.memo(InternalNewTile);
